import { RefObject, useEffect, useState } from 'react';

const DEFAULT_SCROLL_SPEED = 2;

type UseDragToScrollOptions = {
    disableScroll?: boolean;
    scrollSpeed?: number;
};

export function useDragToScroll<T extends HTMLElement>(ref: RefObject<T>, options: UseDragToScrollOptions = {}) {
    const { disableScroll, scrollSpeed = DEFAULT_SCROLL_SPEED } = options;
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
        const element = ref.current;
        if (!element || disableScroll) {
            setIsDragging(false);
            return;
        }

        const handleMouseDown = (e: MouseEvent) => {
            setIsDragging(true);
            setStartX(e.pageX - element.offsetLeft);
            setScrollLeft(element.scrollLeft);
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) {
                return;
            }
            e.preventDefault();
            const x = e.pageX - element.offsetLeft;
            const walk = (x - startX) * scrollSpeed;
            element.scrollLeft = scrollLeft - walk;
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        element.addEventListener('mousedown', handleMouseDown);
        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseup', handleMouseUp);
        element.addEventListener('mouseleave', handleMouseUp);

        return () => {
            element.removeEventListener('mousedown', handleMouseDown);
            element.removeEventListener('mousemove', handleMouseMove);
            element.removeEventListener('mouseup', handleMouseUp);
            element.removeEventListener('mouseleave', handleMouseUp);
        };
    }, [isDragging, scrollSpeed, startX, scrollLeft, ref, disableScroll]);

    return {
        isDragging,
    };
}
